import type * as React from 'react';

import * as MenubarPrimitive from '@radix-ui/react-menubar';
import { Check, ChevronRight, Circle } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { cs } from '@/_shared';

import { Spinner } from './progress';
import type { SpinnerProps } from './progress/types';

const MenubarMenu = MenubarPrimitive.Menu;

const MenubarGroup = MenubarPrimitive.Group;

const MenubarPortal = MenubarPrimitive.Portal;

const MenubarSub = MenubarPrimitive.Sub;

const MenubarRadioGroup = MenubarPrimitive.RadioGroup;

const Menubar = ({ ref, ...props }: React.ComponentProps<typeof MenubarPrimitive.Root>) => (
  <MenubarPrimitive.Root ref={ref} {...props} />
);

const MenubarTrigger = ({ ref, className, ...props }: React.ComponentProps<typeof MenubarPrimitive.Trigger>) => (
  <MenubarPrimitive.Trigger ref={ref} {...props} className={twMerge('outline-hidden focus:ring-0', className)} />
);

const MenubarSubTrigger = ({
  ref,
  className,
  inset,
  children,
  ...props
}: React.ComponentProps<typeof MenubarPrimitive.SubTrigger> & {
  inset?: boolean;
}) => (
  <MenubarPrimitive.SubTrigger
    ref={ref}
    className={twMerge(
      'flex cursor-default items-center rounded-xs px-2 py-1.5 text-sm outline-hidden select-none',
      inset && 'pl-8',
      className
    )}
    {...props}
  >
    {children}
    <ChevronRight className="ml-auto size-4" />
  </MenubarPrimitive.SubTrigger>
);

const MenubarSubContent = ({ ref, className, ...props }: React.ComponentProps<typeof MenubarPrimitive.SubContent>) => (
  <MenubarPrimitive.SubContent
    ref={ref}
    className={twMerge(
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-32 overflow-hidden rounded-xl border p-1',
      className
    )}
    {...props}
  />
);

const MenubarContent = ({
  ref,
  className,
  align = 'start',
  alignOffset = -4,
  sideOffset = 8,
  children,
  ...props
}: React.ComponentProps<typeof MenubarPrimitive.Content>) => (
  <MenubarPrimitive.Portal>
    <MenubarPrimitive.Content
      ref={ref}
      align={align}
      alignOffset={alignOffset}
      sideOffset={sideOffset}
      className={twMerge(
        'border-c-border text-c-card-muted data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-99999 min-w-32 overflow-hidden rounded-xl border bg-white px-0 py-2.5 shadow-lg shadow-gray-500/10 dark:bg-gray-950 dark:shadow-gray-800/50',
        'rounded-xl',
        cs('rounded-4xl'),
        className
      )}
      {...props}
    >
      <div className="px-1">{children}</div>
    </MenubarPrimitive.Content>
  </MenubarPrimitive.Portal>
);

const MenubarItem = ({
  ref,
  className,
  children,
  inset,
  showLoader = false,
  disabled,
  loaderSize = 'xs',
  ...props
}: React.ComponentProps<typeof MenubarPrimitive.Item> & {
  inset?: boolean;
  showLoader?: boolean;
  loaderSize?: SpinnerProps['size'];
}) => (
  <MenubarPrimitive.Item
    ref={ref}
    className={twMerge(
      'text-c-card-muted relative flex cursor-pointer items-center gap-x-2.5 rounded-xl px-3 py-2 pt-2.25 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50',
      'hover:text-accent hover:bg-gray-200 dark:hover:bg-gray-900 dark:hover:text-white',
      'active:text-accent active:bg-gray-200 dark:active:bg-gray-900 dark:active:text-white',
      'focus:text-accent focus:bg-gray-200 dark:focus:bg-gray-900 dark:focus:text-white',
      inset && 'pl-8',
      'py-1.5 font-sans text-sm leading-normal font-medium text-inherit antialiased',
      className
    )}
    {...props}
    disabled={disabled || showLoader}
  >
    {children}
    {showLoader ? (
      <div className="absolute inset-0 z-10 flex items-center justify-center" aria-hidden={true}>
        <Spinner size={loaderSize} />
      </div>
    ) : null}
  </MenubarPrimitive.Item>
);

const MenubarCheckboxItem = ({
  ref,
  className,
  children,
  checked,
  ...props
}: React.ComponentProps<typeof MenubarPrimitive.CheckboxItem>) => (
  <MenubarPrimitive.CheckboxItem
    ref={ref}
    className={twMerge(
      'focus:bg-accent relative flex cursor-default items-center rounded-xs py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50',
      className
    )}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <MenubarPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </MenubarPrimitive.ItemIndicator>
    </span>
    {children}
  </MenubarPrimitive.CheckboxItem>
);

const MenubarRadioItem = ({
  ref,
  className,
  children,
  ...props
}: React.ComponentProps<typeof MenubarPrimitive.RadioItem>) => (
  <MenubarPrimitive.RadioItem
    ref={ref}
    className={twMerge(
      'focus:bg-accent relative flex cursor-default items-center rounded-xs py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50',
      className
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <MenubarPrimitive.ItemIndicator>
        <Circle className="h-2 w-2 fill-current" />
      </MenubarPrimitive.ItemIndicator>
    </span>
    {children}
  </MenubarPrimitive.RadioItem>
);

const MenubarLabel = ({
  ref,
  className,
  inset,
  ...props
}: React.ComponentProps<typeof MenubarPrimitive.Label> & {
  inset?: boolean;
}) => (
  <MenubarPrimitive.Label
    ref={ref}
    className={twMerge('px-2 py-1.5 text-sm font-semibold', inset && 'pl-8', className)}
    {...props}
  />
);

const MenubarSeparator = ({ ref, className, ...props }: React.ComponentProps<typeof MenubarPrimitive.Separator>) => (
  <MenubarPrimitive.Separator
    ref={ref}
    className={twMerge('bg-outline -mx-1 my-1 h-px dark:bg-gray-600', className)}
    {...props}
  />
);

function MenubarShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return <span className={twMerge('ml-auto text-xs tracking-widest', className)} {...props} />;
}

export {
  Menubar,
  MenubarCheckboxItem,
  MenubarContent,
  MenubarGroup,
  MenubarItem,
  MenubarLabel,
  MenubarMenu,
  MenubarPortal,
  MenubarRadioGroup,
  MenubarRadioItem,
  MenubarSeparator,
  MenubarShortcut,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
};
